import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Typography, Table } from 'antd'
import { debounce } from 'lodash'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import { HorizontalDivider } from 'portal/components/Navigation/Main/Toolbar/Divider'

import { EVENTS } from 'portal/lib/constants'
import { useMixpanelEvents } from './mixpanel'

import { DateFormatFull } from 'portal/lib/functions'
import { useResponsive, useModuleIds } from 'portal/lib/hooks'
import {
  ColourButton,
  Flex,
  icons,
  SideBar,
  SmallSpinner,
  Spacer,
  Spinner
} from 'portal/lib/primitives'

import { ReportEventCell } from './ReportEventCell'
import { useEventsLoader } from './useEventsLoader'

import './EventsTable.scss'

const { Title, Text } = Typography

const EMPTY_ARRAY = []

export function EventLog({ startDate, endDate }) {
  const [filteredInfo, setFilteredInfo] = useState({})

  const { moduleName } = useModuleIds()

  const { isMobile, isSmall } = useResponsive()
  const sourceTypeFilters = useMemo(() => {
    return EVENTS.SOURCE_TYPE
      ? Object.values(EVENTS.SOURCE_TYPE).map(item => ({
          text: item.label,
          value: item.key
        }))
      : []
  }, [])

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const {
    loading,
    error,
    items,
    handlePageChange,
    hasNextPage,
    showTotalRecords
  } = useEventsLoader(
    startDate,
    endDate,
    EMPTY_ARRAY,
    filteredInfo.sourceType || EMPTY_ARRAY
  )

  const tableId = 'EventsTable'

  const { trackOpen } = useMixpanelEvents()

  useEffect(() => {
    trackOpen()
  }, [trackOpen])

  const formattedDisplayItems = [...items]

  const renderDescription = (text, item, index) => {
    if (index === 0 || !item.subDescription) {
      return text
    }
    return (
      <div>
        <div>{text}</div>
        <div className="subText">{item.subDescription}</div>
      </div>
    )
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      className: 'createdAt',
      width: isMobile || isSmall ? '7rem' : '17.5%',
      render: (text, _, index) => {
        return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
          isMobile || isSmall
            ? {
                timeZone: projectTimeZone,
                year: '2-digit',
                month: 'numeric',
                day: 'numeric'
              }
            : DateFormatFull(projectTimeZone),
          { locale: Intl.DateTimeFormat().resolvedOptions().locale }
        )
      }
    },
    {
      title: 'Type',
      dataIndex: 'sourceType',
      className: 'sourceType',
      width: '12.5%',
      filters: sourceTypeFilters,
      filteredValue: filteredInfo.sourceType || null,
      onFilter: (value, record) => record.sourceType === value,
      render: (text, _, index) => EVENTS.SOURCE_TYPE[text]?.label ?? text
    },
    {
      title: 'Description',
      dataIndex: 'description',
      className: 'description',
      width: '57.5%',
      render: (text, item, index) => renderDescription(text, item, index)
    },
    {
      title: '',
      dataIndex: 'sourceType',
      className: 'links',
      width: '12.5%',
      align: 'right',
      render: (text, record, index) => {
        return text === 'Report' && <ReportEventCell item={record} />
      }
    }
  ]

  const style = { width: '100%' }
  if (isSmall && !isMobile) {
    style.minWidth = '32.5rem'
  }

  // Infinite Scroll
  useEffect(() => {
    const handleTableScroll = event => {
      const maxScroll = event.target.scrollHeight - event.target.clientHeight
      const currentScroll = event.target.scrollTop
      if (currentScroll >= maxScroll - 65) {
        // load more data
        handlePageChange()
      }
    }

    const debouncedHandleTableScroll = debounce(
      event => handleTableScroll(event),
      100,
      {
        leading: true
      }
    )

    const tableContent = document.querySelector('.ant-table-body')
    if (tableContent) {
      // Only apply the Event Listener if there is more data to be loaded
      if (hasNextPage) {
        tableContent.addEventListener('scroll', debouncedHandleTableScroll)
      }
    }

    // Using useEffect return function to remove the Event Listener on unload
    return () => {
      if (tableContent) {
        tableContent.removeEventListener('scroll', debouncedHandleTableScroll)
      }
    }
  }, [handlePageChange, hasNextPage])

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // `extra.currentDataSource` contains the filtered data
    setFilteredInfo(filters)
  }

  const scrollToTop = () => {
    const tableContent = document.querySelector('.ant-table-body')
    tableContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  return (
    <>
      <Flex.Row amount={0} style={{ width: '99%', alignItems: 'baseline' }}>
        <Title level={2} id="Heading" style={{ marginBottom: '0' }}>
          {moduleName === 'waste' ? 'Waste' : 'Delivery'} Events
        </Title>
        <Flex.By amount={1} />
        <SideBar.GlobalDatePicker />
      </Flex.Row>
      <HorizontalDivider style={{ width: '99%' }} />
      {error && (
        <Text type="danger">
          There was a problem loading this page, please try again
        </Text>
      )}
      {(!loading || formattedDisplayItems.length > 30) && !error && (
        <>
          <TableWrapper>
            <Table
              id={tableId}
              columns={columns}
              onChange={handleTableChange}
              dataSource={formattedDisplayItems}
              rowKey="eventKey"
              pagination={false}
              scroll={{ y: 'calc(100vh - 340px)' }}
              sticky
              style={style}
              rowClassName={(record, index) => {
                if (record.status === 'Closed') {
                  return 'event-row-closed-light'
                }
                return 'event-row-open-light'
              }}
              loading={{
                spinning: loading,
                indicator: (
                  <div>
                    <SmallSpinner />
                  </div>
                )
              }}
              locale={{
                emptyText: (
                  <ZeroRecordsFooter>
                    <icons.ManageSearch />
                    <div>
                      No events matched your search. Try increasing the date
                      range.
                    </div>
                  </ZeroRecordsFooter>
                )
              }}
              onRow={(record, rowIndex) => {
                if (rowIndex === 0) {
                  return {}
                }
                return {
                  'data-testid': 'EventsTableRow'
                }
              }}
              footer={currentPageData => {
                return null
              }}
            />
          </TableWrapper>
          <TableFooter>
            {showTotalRecords()}
            <ButtonContainer>
              <BTTButton id="BTTButton" onClick={scrollToTop}>
                Back to top
                <Spacer.Fine />
                <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
              </BTTButton>
            </ButtonContainer>
          </TableFooter>
        </>
      )}
      {loading && formattedDisplayItems.length < 31 && <Spinner />}
    </>
  )
}

const TableWrapper = styled.div`
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.5rem 1.25rem 1.25rem 1.25rem;
  margin-top: 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`

const ZeroRecordsFooter = styled.div`
  display: flex;
  justify-content: center;
`

const TableFooter = styled('div')`
  position: relative;
  width: 99%;
`

const ButtonContainer = styled('div')`
  position: absolute;
  right: 0;
  top: 0.75rem;
`
const BTTButton = styled(ColourButton.Basic)`
  line-height: 1;
  width: auto;
`

EventLog.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object
}
